import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Autocomplete,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext, emitirEcfDgii } from 'context';
import { FP, fpInputSx } from 'components/financepro';
import {
  cuentasPosteablesDeCatalogo,
  labelCentroCosto,
  labelCuentaCatalogo,
} from 'contabilidad/entradaDiarioShared';
import { fechaEmisionDgii } from 'facturapos/documentoEmpresarial';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const enviarMotorContableActivo = (valor) => String(valor || '').trim().toUpperCase() === 'S';

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const TIPO_MOTOR_COMPRA_CONTADO = 'Compras Electronicas Contado';
const TIPO_MOTOR_COMPRA_CREDITO = 'Compras Electronicas Credito';

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const reglaCuentaEsJson = (regla) => {
  const aplica = String(regla?.aplica_a || 'cuenta').trim().toLowerCase();
  if (aplica === 'json') return true;
  const raw = String(regla?.cuentacontable || '').trim();
  return raw.startsWith('[') || raw.startsWith('{');
};

const reglaEsDesembolso = (regla) =>
  String(regla?.codigo_regla || '').trim().toLowerCase() === 'desembolso';

const cuentaDesdeFormasPago = (formaPago, catalogo) => {
  const directa = String(formaPago?.cuenta_contable || '').trim();
  if (directa) return directa;
  const doc = String(formaPago?.documento || '').trim().toLowerCase();
  if (!doc) return '';
  const fila = (catalogo || []).find(
    (f) => String(f?.documento || '').trim().toLowerCase() === doc
  );
  return String(fila?.cuenta_contable || '').trim();
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || '').trim() !== tipo) return false;
    if (reglaEsDesembolso(r) || reglaCuentaEsJson(r)) return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || '').trim() === tipo);
};

const lineasCuentasJsonMotor = (lineas) =>
  (lineas || [])
    .map((ln) => ({
      cuenta: String(ln?.cuenta || '').trim(),
      monto: redondearDinero2(parseFloat(ln?.valor ?? ln?.monto) || 0),
      localidad: String(ln?.localidad || '').trim(),
    }))
    .filter((x) => x.cuenta && x.monto > 0);

const construirDocumentoMotorCompra = (reglas, ctx) => {
  const cuentasJson = lineasCuentasJsonMotor(ctx.lineas);
  const montos = ctx.montos || {};
  const valor = redondearDinero2(ctx.valorTotal);
  const doc = {
    tipodocumento: ctx.tipodocumento,
    nfactura: ctx.nfactura,
    documento: ctx.documento,
    numero: ctx.numero,
    suplidor: ctx.suplidor,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: cuentasJson,
    permitir_descuadre: true,
  };
  const cuentaDesembolso = String(ctx.cuentaDesembolso || '').trim();
  const lineasDesembolso =
    cuentaDesembolso && valor > 0 ? [{ cuenta: cuentaDesembolso, monto: valor }] : [];
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    if (reglaEsDesembolso(regla)) {
      doc[codigo] = lineasDesembolso;
      return;
    }
    if (reglaCuentaEsJson(regla)) {
      doc[codigo] = cuentasJson;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const rncValido = (valor) => {
  const digits = String(valor || '').replace(/\D/g, '');
  if (!digits || digits === '999999') return '';
  if (digits.length === 9 || digits.length === 11) return digits;
  return '';
};

const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const labelSuplidor = (s) => {
  if (!s) return '';
  const rnc = String(s.rnc || '').trim();
  return `${s.id} — ${s.descrip || ''}${rnc ? ` (${rnc})` : ''}`;
};

const labelFormaPago = (f) => {
  if (!f) return '';
  const nombre = String(f.documento || '').trim() || '(sin nombre)';
  const cuenta = String(f.cuenta_contable || '').trim();
  return cuenta ? `${nombre} · ${cuenta}` : nombre;
};

/** DGII IndicadorBienoServicio: 1 = bien (compra), 2 = servicio. */
const TIPO_COMPRA = '1';
const TIPO_SERVICIO = '2';

/**
 * Captura reutilizable de compras electrónicas (E41).
 * El usuario indica si es compra o servicio y el concepto; guarda y firma el e-CF.
 */
const FormularioCompraElectronica = ({
  open = false,
  onClose,
  puedeModificar,
  tiposDocumentosFiscales,
  tipoDocumento,
  onTipoDocumentoChange,
  onCreada,
}) => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    comprobanteelectronico,
    nombreNegocio,
    rnc_empresa,
    direccion_empresa,
    modulos,
    enviarMotorContable,
  } = controller;
  const usaCuentasContables = moduloContabilidadInstalado(modulos);
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const emisionElectronica =
    String(comprobanteelectronico || '').trim().toUpperCase() === 'S';

  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [tipoPago, setTipoPago] = useState('Credito');
  const [formaPagoSel, setFormaPagoSel] = useState(null);
  const [referenciaPago, setReferenciaPago] = useState('');
  const [tasa, setTasa] = useState('1.00');
  const [porcRetencion, setPorcRetencion] = useState('0.02');
  const [tipoBienServicio, setTipoBienServicio] = useState(TIPO_SERVICIO);
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [suplidores, setSuplidores] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);
  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const [cargandoCentros, setCargandoCentros] = useState(false);
  const [cuentaLineaSel, setCuentaLineaSel] = useState(null);
  const [descripcionLineaStr, setDescripcionLineaStr] = useState('');
  const [centroLineaSel, setCentroLineaSel] = useState(null);
  const [valorLineaStr, setValorLineaStr] = useState('');
  const [lineasDistribucion, setLineasDistribucion] = useState([]);

  const credenciales = useMemo(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '', empresa: empresaFiscal,
    }),
    [selectedCompany, usuarioLog, claveLog]
  );

  const cargarMaestros = useCallback(async () => {
    if (!usuarioLog) return;
    const post = async (url) => {
      const res = await fetch(`${servidor}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credenciales),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, data };
    };
    setCargandoFormas(true);
    try {
      const [supRes, formasApi, formasSinApi] = await Promise.all([
        post('/contabilidadbackend_listarsuplidores'),
        post('/api/configuracionbackend_listarformasdepago'),
        post('/configuracionbackend_listarformasdepago'),
      ]);
      setSuplidores(supRes.data.suplidores || []);
      const formas = formasApi.ok
        ? formasApi.data.formas_pago
        : formasSinApi.ok
          ? formasSinApi.data.formas_pago
          : [];
      setFormasPago(Array.isArray(formas) ? formas : []);
      if (!supRes.ok) showError('No se pudieron cargar los suplidores');
      if (!formasApi.ok && !formasSinApi.ok) {
        showError(
          formasApi.data.Error ||
            formasSinApi.data.Error ||
            'No se pudieron cargar las formas de pago'
        );
      }
    } catch (_e) {
      showError('No se pudieron cargar suplidores o formas de pago');
    } finally {
      setCargandoFormas(false);
    }
  }, [usuarioLog, servidor, credenciales, showError]);

  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const cuentasPosteables = useMemo(
    () => cuentasPosteablesDeCatalogo(cuentasCatalogo),
    [cuentasCatalogo]
  );

  const cargarCatalogoCuentas = useCallback(async () => {
    if (!usaCuentasContables || !puedeOperarDb) {
      setCuentasCatalogo([]);
      return;
    }
    setCargandoCatalogo(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcatalogoactualizable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
          busqueda: '',
          orden: 'cuenta',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar el catálogo de cuentas');
        setCuentasCatalogo([]);
        return;
      }
      setCuentasCatalogo(data.cuentas || []);
    } catch {
      showError('Error de conexión al cargar el catálogo');
      setCuentasCatalogo([]);
    } finally {
      setCargandoCatalogo(false);
    }
  }, [usaCuentasContables, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarCentrosCosto = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoCentros(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcentrodecosto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '', empresa: empresaFiscal,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar las localidades');
        setCentrosCosto([]);
        return;
      }
      setCentrosCosto(
        (data.centros || []).map((r) => ({
          id: r.id,
          descrip: (r.descrip || '').trim(),
        }))
      );
    } catch {
      showError('Error de conexión al cargar localidades');
      setCentrosCosto([]);
    } finally {
      setCargandoCentros(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  useEffect(() => {
    if (open) {
      cargarMaestros();
      cargarCatalogoCuentas();
      cargarCentrosCosto();
    }
  }, [open, cargarMaestros, cargarCatalogoCuentas, cargarCentrosCosto]);

  const subtotal = useMemo(() => toNumber(monto), [monto]);
  const itbis = useMemo(() => Math.round(subtotal * 0.18 * 100) / 100, [subtotal]);
  const retencion = useMemo(
    () => Math.round(subtotal * toNumber(porcRetencion) * 100) / 100,
    [subtotal, porcRetencion]
  );
  const totalPagar = useMemo(
    () => Math.round((subtotal + itbis - retencion) * 100) / 100,
    [subtotal, itbis, retencion]
  );

  const requiereReferencia =
    tipoPago === 'Contado' &&
    String(formaPagoSel?.necesita_referencia || 'N').toUpperCase() === 'S';

  useEffect(() => {
    if (!requiereReferencia) setReferenciaPago('');
  }, [requiereReferencia]);

  const docFiscal = useMemo(
    () => tiposDocumentosFiscales.find((t) => t.nombre === tipoDocumento) || null,
    [tiposDocumentosFiscales, tipoDocumento]
  );

  const limpiarFormulario = () => {
    setFecha(hoy);
    setSuplidorSel(null);
    setTipoPago('Credito');
    setFormaPagoSel(null);
    setReferenciaPago('');
    setTasa('1.00');
    setPorcRetencion('0.02');
    setTipoBienServicio(TIPO_SERVICIO);
    setConcepto('');
    setMonto('');
    setCuentaLineaSel(null);
    setDescripcionLineaStr('');
    setCentroLineaSel(null);
    setValorLineaStr('');
    setLineasDistribucion([]);
  };

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'compra_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  const etiquetaLocalidadLinea = useCallback(
    (row) => {
      const id = String(row?.localidad || '').trim();
      if (!id) return '—';
      const c = centrosCosto.find((x) => String(x.id) === id);
      return c ? labelCentroCosto(c) : id;
    },
    [centrosCosto]
  );

  const agregarLineaDistribucion = () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    let cuentaCod = '';
    let nombreCuenta = '';
    if (usaCuentasContables) {
      if (!cuentaLineaSel) {
        showError('Seleccione una cuenta del catálogo');
        return;
      }
      cuentaCod = String(cuentaLineaSel.cuenta ?? '').trim();
      nombreCuenta = String(cuentaLineaSel.nombre ?? '').trim();
    } else {
      nombreCuenta = (descripcionLineaStr || '').trim();
      if (!nombreCuenta) {
        showError('Indique la descripción de la línea');
        return;
      }
    }
    const centroId =
      centroLineaSel != null &&
      centroLineaSel.id != null &&
      String(centroLineaSel.id).trim() !== ''
        ? String(centroLineaSel.id).trim()
        : '';
    const raw = (valorLineaStr || '').trim().replace(',', '.');
    if (raw === '') {
      showError('Indique el valor de la línea');
      return;
    }
    const valorNum = parseFloat(raw);
    if (!Number.isFinite(valorNum)) {
      showError('Valor numérico inválido');
      return;
    }
    setLineasDistribucion((prev) => [
      ...prev,
      {
        key: `${Date.now()}-${Math.floor(Math.random() * 1e15)}`,
        cuenta: cuentaCod,
        descripcion: nombreCuenta,
        localidad: centroId,
        valor: valorNum,
      },
    ]);
    setCuentaLineaSel(null);
    setDescripcionLineaStr('');
    setValorLineaStr('');
  };

  const eliminarLineaDistribucion = (key) => {
    if (!puedeModificar) return;
    setLineasDistribucion((prev) => prev.filter((r) => r.key !== key));
  };

  const totalDistribGasto = useMemo(
    () => lineasDistribucion.filter((r) => r.valor > 0).reduce((s, r) => s + r.valor, 0),
    [lineasDistribucion]
  );
  const totalDistribDeduc = useMemo(
    () => lineasDistribucion.filter((r) => r.valor < 0).reduce((s, r) => s + r.valor, 0),
    [lineasDistribucion]
  );
  const totalDistribucion = useMemo(
    () => redondearDinero2(lineasDistribucion.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0)),
    [lineasDistribucion]
  );

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const cellEllipsisSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 0,
  };

  const navyBtnSx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const enviarAlMotorContableCompra = useCallback(
    async (ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!selectedCompany?.db_empresa) return;
      const tipoDoc = ctxDocumento.tipodocumento;
      const formaPagoDoc = String(ctxDocumento.formaPago?.documento || '').trim();
      try {
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            tipodocumento: tipoDoc,
            ...(formaPagoDoc ? { forma_pago: formaPagoDoc } : {}),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(
            `No hay reglas del motor para ${tipoDoc}; no se registró en diario/histdia`
          );
          return;
        }
        const cuentaListada = String(reglasData.cuenta_forma_pago || '').trim();
        const cuentaDesembolso =
          cuentaListada || cuentaDesdeFormasPago(ctxDocumento.formaPago, ctxDocumento.formasPago);
        if (reglasDoc.some(reglaEsDesembolso) && formaPagoDoc && !cuentaDesembolso) {
          showWarning(
            `La forma de pago ${formaPagoDoc} no tiene cuenta contable en formas de pago`
          );
        }
        const payloadDocumento = construirDocumentoMotorCompra(reglasDoc, {
          ...ctxDocumento,
          cuentaDesembolso,
        });
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar la compra al motor contable');
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar la compra al motor contable');
      }
    },
    [modulos, enviarMotorContable, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  const guardarCompra = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear compras electrónicas.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione el suplidor');
      return;
    }
    if (!rncValido(suplidorSel.rnc)) {
      showError('El suplidor debe tener RNC o cédula DGII válido (9 u 11 dígitos)');
      return;
    }
    if (!tipoBienServicio) {
      showError('Indique si es una compra o un servicio');
      return;
    }
    if (!concepto.trim()) {
      showError('El concepto es requerido');
      return;
    }
    if (subtotal <= 0) {
      showError('El monto debe ser mayor que cero');
      return;
    }
    if (!tipoDocumento || !docFiscal) {
      showError('Seleccione el tipo de documento fiscal E41');
      return;
    }
    if (!emisionElectronica) {
      showError('La empresa no tiene habilitada la emisión de comprobantes electrónicos');
      return;
    }
    if (tipoPago === 'Contado') {
      if (!formaPagoSel?.documento) {
        showError('Para contado debe seleccionar la forma de pago');
        return;
      }
      if (requiereReferencia && !referenciaPago.trim()) {
        showError('La forma de pago requiere una referencia');
        return;
      }
    }
    if (lineasDistribucion.length === 0) {
      showError('Agregue al menos una línea de distribución del gasto o compra');
      return;
    }
    const sumaLineas = redondearDinero2(
      lineasDistribucion.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0)
    );
    if (Math.abs(sumaLineas - redondearDinero2(totalPagar)) > 0.02) {
      showError(
        `El total de la distribución debe coincidir con el monto. Distribución: $${formatMoney(sumaLineas)} · Monto: $${formatMoney(totalPagar)}.`
      );
      return;
    }
    if (usaCuentasContables) {
      const sinCuenta = lineasDistribucion.some((ln) => !(ln.cuenta || '').trim());
      if (sinCuenta) {
        showError('Cada línea de distribución debe tener una cuenta contable');
        return;
      }
    }

    setGuardando(true);
    try {
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_crearcompraelectronica`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            fecha,
            suplidor_id: String(suplidorSel.id),
            descripcion: concepto.trim(),
            tipo_bien_servicio: tipoBienServicio,
            monto: subtotal,
            monto_total: totalPagar,
            tasa: toNumber(tasa) || 1,
            tipo_pago: tipoPago,
            banco_referencia: formaPagoSel
              ? `${formaPagoSel.documento}${referenciaPago.trim() ? ` · ${referenciaPago.trim()}` : ''}`
              : '',
            porcentaje_retencion: toNumber(porcRetencion),
            tipo_documento: tipoDocumento,
            comprobanteelectronico: 'S',
            codigocomprobante: codigoTipoDocumento(docFiscal) || '41',
            lineas_distribucion: lineasDistribucion.map((ln) => ({
              cuenta: usaCuentasContables ? ln.cuenta : '',
              descripcion: ln.descripcion,
              localidad: ln.localidad,
              valor: ln.valor,
            })),
            modulos,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'No se pudo guardar la compra electrónica');
        return;
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      let filename = 'compra_ecf.xlsx';
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
      const ncf = response.headers.get('X-Comprobante-Fiscal') || '';
      const numero = parseInt(response.headers.get('X-Numero-Desembolso') || '0', 10);
      const fechaVencimiento = fechaEmisionDgii(response.headers.get('X-Fecha-Valida') || '');
      const rncComprador = response.headers.get('X-Rnc-Comprador') || rncValido(suplidorSel.rnc);
      const razonSocial = response.headers.get('X-Razon-Social') || suplidorSel.descrip || '';
      const subtotalHdr = toNumber(response.headers.get('X-Subtotal')) || subtotal;
      const itbisHdr = toNumber(response.headers.get('X-Itbis')) || itbis;
      const retencionHdr = toNumber(response.headers.get('X-Retencion')) || retencion;

      descargarArchivoECF(blob, filename);
      showSuccess(`Desembolso ${numero} guardado${ncf ? ` · ${ncf}` : ''}`);

      const payloadEmision = {
        comprobanteelectronico: emisionElectronica ? 'S' : 'N',
        rnc: rncComprador,
        razonsocial: razonSocial,
        comprobante: ncf,
        tipocomprobante: tipoDocumento,
        codigocomprobante: codigoTipoDocumento(docFiscal) || '41',
        fechavencimientosecuencia: fechaVencimiento,
        items: [
          {
            item: concepto.trim(),
            cantidad: 1,
            valor: subtotalHdr,
            descuento: 0,
            tasaitbis: 18,
            montoitbisretenido: itbisHdr,
            montoisrretenido: retencionHdr,
            indicadorbienoservicio: tipoBienServicio,
          },
        ],
        password: claveLog, empresa: empresaFiscal,
        usuario: usuarioLog,
        rncemisor: emisorFiscal.rnc || '',
        razonsocialemisor: emisorFiscal.nombre || '',
        direccionemisor: emisorFiscal.direccion || '',
      };

      const emision = await emitirEcfDgii(servidor, payloadEmision);
      const estadoEcf = String(emision?.estado || '').trim().toLowerCase();
      const dgiiFallido = estadoEcf === 'rechazado' || estadoEcf === 'error';
      const encfUsado = String(emision?.encf || ncf || '').trim();

      const sincronizarEstado = async (estado, fechaFirma = '') => {
        if (!encfUsado || !estado) return;
        try {
          await fetch(`${servidor}/api/facturaselectronicasdgii_actualizarestadoecf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              comprobante: encfUsado,
              estado,
              fecha_firma: fechaFirma,
            }),
          });
        } catch (_ignored) {
          /* best-effort */
        }
      };

      if (emision.ok) {
        descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
        if (emision.xml_rfce) {
          descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
        }
        const etiquetas = {
          aceptado: 'aceptado por la DGII',
          aceptado_condicional: 'aceptado condicional por la DGII',
          en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
          rechazado: 'RECHAZADO por la DGII',
          pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
          error: 'firmado; el envío a la DGII falló',
        };
        const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
          .map((m) => `${m.valor || m.mensaje || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
          .filter((t) => t.trim())
          .join(' · ');
        const mensaje =
          `e-CF ${emision.encf} ${etiquetas[estadoEcf] || emision.estado}` +
          ` · código ${emision.codigo_seguridad}` +
          (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
        if (dgiiFallido) showError(mensaje);
        else showSuccess(mensaje);
        await sincronizarEstado(
          estadoEcf || 'aceptado',
          emision.fecha_firma || emision.fecha || new Date().toISOString().slice(0, 10)
        );
      } else {
        showError(emision.error || 'No se pudo emitir el e-CF ante la DGII');
        await sincronizarEstado('error');
      }

    
      if (!emision.ok || dgiiFallido) {
        try {
          await fetch(`${servidor}/api/facturaselectronicasdgii_revertircompraelectronica`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              numero,
              comprobante: encfUsado,
            }),
          });
        } catch (_ignored) {
          /* best-effort */
        }
        showError(
          `Desembolso ${numero} revertido (e-CF rechazado o con error). El próximo usa la secuencia siguiente.`
        );
        if (typeof onCreada === 'function') onCreada({ fallido: true, ncf: encfUsado });
        return;
      }
      
      const compraVista = {
        numero,
        descripcion: concepto.trim(),
        total: subtotalHdr,
        itbis: itbisHdr,
        retencion: retencionHdr,
        suplidor: suplidorSel.id,
        suplidor_nombre: razonSocial,
        rnc: rncComprador,
      };
      const totalMotor = redondearDinero2(subtotalHdr + itbisHdr - retencionHdr);
      console.log('totalMotor', totalMotor);
      await enviarAlMotorContableCompra({
        tipodocumento:
          tipoPago === "Contado" ? TIPO_MOTOR_COMPRA_CONTADO : TIPO_MOTOR_COMPRA_CREDITO,
        nfactura: ncf || String(numero || ''),
        documento: 'DS',
        numero,
        suplidor: String(suplidorSel.id),
        fecha,
        tasa: toNumber(tasa) || 1,
        usuario: usuarioLog || '',
        concepto: concepto.trim(),
        valorTotal: totalMotor,
        lineas: lineasDistribucion,
        montos: {
          subtotal: redondearDinero2(subtotalHdr),
          monto: redondearDinero2(subtotalHdr),
          itbis: redondearDinero2(itbisHdr),
          retencion: redondearDinero2(retencionHdr),
          total: totalMotor,
        },
        formaPago: formaPagoSel,
        formasPago,
      });
      limpiarFormulario();
      if (typeof onClose === 'function') onClose();
      if (typeof onCreada === 'function') {
        onCreada({
          compras: [compraVista],
          ncf,
          emisionData: {
            ...emision,
            fechavencimientosecuencia: fechaVencimiento,
            fechavencimiento: fechaVencimiento,
          },
          rncComprador,
          razonSocialComprador: razonSocial,
        });
      }
    } catch (_e) {
      showError('Error de conexión al guardar la compra electrónica');
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = () => {
    if (guardando) return;
    if (typeof onClose === 'function') onClose();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={cerrar}
      fullScreen
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 0,
          m: 0,
          width: '100vw',
          maxWidth: '100vw',
          height: '100vh',
          maxHeight: '100vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          color: FP.color.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1.5,
          borderBottom: `1px solid ${FP.color.border}`,
        }}
      >
        Nueva compra electrónica
        <IconButton onClick={cerrar} disabled={guardando} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: FP.color.surface, p: { xs: 2, md: 3 }, height: '100%', overflow: 'auto' }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5, mt: 1 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del documento
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {!puedeModificar && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Solo lectura: no puede emitir compras electrónicas.
              </Alert>
            )}
            {!emisionElectronica && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                La empresa no tiene habilitada la emisión de comprobantes electrónicos.
              </Alert>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  options={suplidores}
                  value={suplidorSel}
                  onChange={(_e, v) => setSuplidorSel(v)}
                  getOptionLabel={labelSuplidor}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  disabled={!puedeModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      placeholder="Buscar suplidor"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  label="Documento fiscal"
                  value={tipoDocumento}
                  onChange={(e) => onTipoDocumentoChange(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Seleccione…</MenuItem>
                  {tiposDocumentosFiscales.map((t) => (
                    <MenuItem key={t.id || t.nombre} value={t.nombre}>
                      {t.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo de pago"
                  value={tipoPago}
                  onChange={(e) => {
                    setTipoPago(e.target.value);
                    if (e.target.value !== 'Contado') {
                      setFormaPagoSel(null);
                      setReferenciaPago('');
                    }
                  }}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="Credito">Crédito</MenuItem>
                  <MenuItem value="Contado">Contado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  options={formasPago}
                  loading={cargandoFormas}
                  value={tipoPago === 'Credito' ? null : formaPagoSel}
                  onChange={(_e, v) => {
                    setFormaPagoSel(v);
                    setReferenciaPago('');
                  }}
                  getOptionLabel={labelFormaPago}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  disabled={!puedeModificar || tipoPago !== 'Contado' || cargandoFormas}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Forma de pago"
                      placeholder={
                        tipoPago === 'Contado'
                          ? cargandoFormas
                            ? 'Cargando…'
                            : formasPago.length
                              ? 'Seleccione forma de pago'
                              : 'No hay formas de pago definidas'
                          : 'No aplica en crédito'
                      }
                      helperText={
                        tipoPago === 'Contado' && formaPagoSel
                          ? requiereReferencia
                            ? 'Debe completar la referencia'
                            : 'No requiere referencia'
                          : ' '
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Referencia al pago"
                  value={requiereReferencia ? referenciaPago : ''}
                  onChange={(e) => setReferenciaPago(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar || !requiereReferencia}
                  required={requiereReferencia}
                  placeholder={requiereReferencia ? 'Nº cheque, transferencia, etc.' : ''}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Tasa"
                  value={tasa}
                  onChange={(e) => setTasa(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Retención ISR"
                  value={porcRetencion}
                  onChange={(e) => setPorcRetencion(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="0.02">2%</MenuItem>
                  <MenuItem value="0.10">10%</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de la factura
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo"
                  value={tipoBienServicio}
                  onChange={(e) => setTipoBienServicio(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  helperText="Compra = bien · Servicio = servicio"
                  sx={fpInputSx}
                >
                  <MenuItem value={TIPO_COMPRA}>Compra</MenuItem>
                  <MenuItem value={TIPO_SERVICIO}>Servicio</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  label="Concepto"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  placeholder={
                    tipoBienServicio === TIPO_COMPRA
                      ? 'Descripción de la compra'
                      : 'Descripción del servicio'
                  }
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Monto"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  Subtotal
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  ${formatMoney(subtotal)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  ITBIS 18%
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  ${formatMoney(itbis)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  ISR retenido
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  ${formatMoney(retencion)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  Total a pagar
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.navy, fontSize: '1.15rem' }}>
                  ${formatMoney(totalPagar)}
                </Typography>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Distribución del gasto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {usaCuentasContables
                  ? 'Cuenta, localidad opcional (sucursales y localidades existentes) y valor. El total de las líneas debe coincidir con el monto.'
                  : 'Descripción, localidad opcional (sucursales y localidades existentes) y valor. Las cuentas contables no están disponibles porque el módulo de contabilidad no está activo.'}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                  {usaCuentasContables ? (
                    <Autocomplete
                      options={cuentasPosteables}
                      loading={cargandoCatalogo}
                      value={cuentaLineaSel}
                      onChange={(_, v) => setCuentaLineaSel(v)}
                      getOptionLabel={(o) => labelCuentaCatalogo(o)}
                      isOptionEqualToValue={(a, b) => a?.id === b?.id}
                      filterOptions={(opts, state) => {
                        const q = (state.inputValue || '').toLowerCase().trim();
                        if (!q) return opts;
                        return opts.filter((c) => {
                          const cod = String(c.cuenta ?? '').toLowerCase();
                          const nom = String(c.nombre ?? '').toLowerCase();
                          return cod.includes(q) || nom.includes(q);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cuenta (catálogo)"
                          placeholder="Código o nombre"
                          size="small"
                          helperText="Cuentas del catálogo que admiten movimientos"
                          sx={fpInputSx}
                        />
                      )}
                      disabled={!puedeModificar}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Descripción"
                      placeholder="Concepto del gasto o compra"
                      size="small"
                      value={descripcionLineaStr}
                      onChange={(e) => setDescripcionLineaStr(e.target.value)}
                      helperText="Sin módulo de contabilidad: indique el concepto, no una cuenta"
                      disabled={!puedeModificar}
                      sx={fpInputSx}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={centrosCosto}
                    loading={cargandoCentros}
                    value={centroLineaSel}
                    onChange={(_, v) => setCentroLineaSel(v)}
                    getOptionLabel={(o) => labelCentroCosto(o)}
                    isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                    filterOptions={(opts, state) => {
                      const q = (state.inputValue || '').toLowerCase().trim();
                      if (!q) return opts;
                      return opts.filter((c) => {
                        const id = String(c.id ?? '').toLowerCase();
                        const d = String(c.descrip ?? '').toLowerCase();
                        return id.includes(q) || d.includes(q);
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Localidad (línea, opcional)"
                        placeholder="Localidad o sucursal"
                        size="small"
                        helperText={
                          centrosCosto.length === 0
                            ? 'Sin localidades — puede dejar vacío'
                            : 'Opcional: sucursales y localidades existentes'
                        }
                        sx={fpInputSx}
                      />
                    )}
                    disabled={!puedeModificar}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Valor"
                    size="small"
                    value={valorLineaStr}
                    onChange={(e) => setValorLineaStr(e.target.value)}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal' }}
                    disabled={!puedeModificar}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', pt: { md: 1 } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={agregarLineaDistribucion}
                    disabled={!puedeModificar || (usaCuentasContables && cargandoCatalogo)}
                    sx={{ ...navyBtnSx, whiteSpace: 'nowrap' }}
                  >
                    Añadir
                  </Button>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total gasto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ${formatMoney(totalDistribGasto)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total deducible
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                    ${formatMoney(totalDistribDeduc)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total distribución (neto)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    ${formatMoney(totalDistribucion)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Monto
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    ${formatMoney(subtotal)}
                  </Typography>
                </Grid>
              </Grid>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto', maxHeight: 320 }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 720, sm: 860 } }}>
                  <colgroup>
                    {(usaCuentasContables
                      ? ['16%', '34%', '26%', '14%', '10%']
                      : ['50%', '26%', '14%', '10%']
                    ).map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      {usaCuentasContables ? (
                        <TableCell>
                          <Typography component="span" sx={cellHeadSx}>
                            Cuenta
                          </Typography>
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Descripción
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Localidad
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Valor
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {lineasDistribucion.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={usaCuentasContables ? 5 : 4}>
                          <Typography variant="body2" color="text.secondary">
                            {usaCuentasContables
                              ? 'No hay líneas. Elija cuenta del catálogo, valor y opcionalmente localidad, luego Añadir.'
                              : 'No hay líneas. Indique descripción, valor y opcionalmente localidad, luego Añadir.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineasDistribucion.map((row) => {
                        const localidadTxt = etiquetaLocalidadLinea(row);
                        return (
                          <TableRow key={row.key} hover>
                            {usaCuentasContables ? (
                              <TableCell
                                sx={{ fontWeight: 700, color: FP.color.textPrimary, ...cellEllipsisSx }}
                                title={row.cuenta || '—'}
                              >
                                {row.cuenta || '—'}
                              </TableCell>
                            ) : null}
                            <TableCell sx={cellEllipsisSx} title={row.descripcion || '—'}>
                              {row.descripcion || '—'}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={localidadTxt}>
                              {localidadTxt}
                            </TableCell>
                            <TableCell align="right">${formatMoney(row.valor)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Eliminar línea">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => eliminarLineaDistribucion(row.key)}
                                    disabled={!puedeModificar}
                                    aria-label="Eliminar línea"
                                    sx={{
                                      color: FP.color.danger,
                                      '&:hover': { bgcolor: FP.color.dangerSoft },
                                      '&.Mui-disabled': { color: FP.color.textMuted },
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 2.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarFormulario}
                disabled={guardando}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={guardarCompra}
                disabled={
                  !puedeModificar ||
                  guardando ||
                  subtotal <= 0 ||
                  !concepto.trim() ||
                  !tipoDocumento ||
                  !emisionElectronica
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: FP.color.navy,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                Guardar y firmar e-CF
              </Button>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default FormularioCompraElectronica;
